import { useCallback, useEffect, useRef, useState } from 'react'

import userApi from '../../../services/api/user'

// Retry helper function with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      const isNetworkError =
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Network request failed') ||
        error.name === 'TypeError' ||
        error.name === 'NetworkError'

      // Only retry on network errors, not on other errors (like 400, 401, etc.)
      if (!isNetworkError || attempt === maxRetries - 1) {
        throw error
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

const useNote = (subsidiaryId, kind = 'healthCheck-summary') => {
  const [note, setNoteState] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const debounceTimerRef = useRef(null)
  const previousNoteRef = useRef('') // Store previous note to revert on error

  const key = `${kind}-${subsidiaryId}`

  // Load note on mount
  useEffect(() => {
    const loadNote = async () => {
      if (!subsidiaryId) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const response = await retryWithBackoff(() =>
          userApi.retrieveNote({
            Key: key,
            Kind: kind,
            SubsidiaryId: subsidiaryId
          })
        )
        if (response?.data) {
          setNoteState(response.data)
          previousNoteRef.current = response.data
        } else {
          setNoteState('')
          previousNoteRef.current = ''
        }
      } catch (err) {
        console.error('Failed to load note after retries:', err)
        // Handle network errors more gracefully
        if (
          err.message?.includes('Failed to fetch') ||
          err.message?.includes('NetworkError') ||
          err.message?.includes('Network request failed')
        ) {
          setError('Unable to connect to server. Please check your connection.')
        } else {
          setError(err.message || 'Failed to load note')
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadNote()
  }, [subsidiaryId, key, kind])

  // Save note with debounce
  const saveNote = useCallback(
    async newNote => {
      if (!subsidiaryId) return

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Store previous value from ref before optimistic update
      const previousNote = previousNoteRef.current

      // Optimistically update UI
      setNoteState(newNote)
      setError(null)

      // Debounce the API call (wait 500ms after last change)
      return new Promise((resolve, reject) => {
        debounceTimerRef.current = setTimeout(async () => {
          try {
            setIsSaving(true)
            await retryWithBackoff(() =>
              userApi.addOrUpdateNote({
                Key: key,
                Kind: kind,
                Data: newNote,
                SubsidiaryId: subsidiaryId
              })
            )
            // Update previous note ref on success
            previousNoteRef.current = newNote
            setIsSaving(false)
            resolve()
          } catch (err) {
            console.error('Failed to save note after retries:', err)
            // Revert to previous value on persistent error
            setNoteState(previousNote)
            previousNoteRef.current = previousNote

            // Handle network errors more gracefully
            if (
              err.message?.includes('Failed to fetch') ||
              err.message?.includes('NetworkError') ||
              err.message?.includes('Network request failed')
            ) {
              setError('Unable to connect to server. Please check your connection.')
            } else {
              setError(err.message || 'Failed to save note')
            }
            setIsSaving(false)
            reject(err)
          }
        }, 500)
      })
    },
    [key, kind, subsidiaryId]
  )

  return {
    note,
    setNote: saveNote,
    isLoading,
    isSaving,
    error
  }
}

export default useNote
